import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useChatStore } from "@/hooks/useChatStore";
import type { FeedbackRating } from "./FeedbackButtons";

/**
 * Thumbs up/down on assistant messages, plus the "what went wrong?" dialog a
 * thumbs-down opens.
 *
 * Ratings live here rather than in ChatStore because they are per-mount UI
 * state hydrated from whatever message page the store just loaded — session
 * loading calls `reset()` then `hydrate()` with the fetched messages.
 */
export interface UseChatFeedbackResult {
  messageFeedback: Record<string, FeedbackRating>;
  feedbackLoadingId: string | null;
  feedbackDialog: { open: boolean; messageId: string | null };
  feedbackComment: string;
  setFeedbackComment: (value: string) => void;
  /** Adopt the ratings carried by a freshly loaded page of messages. */
  hydrate: (messages: Array<{ id: string; feedbackRating?: string | null }>) => void;
  /** Drop all ratings — a different session's messages are about to load. */
  reset: () => void;
  onMessageFeedback: (messageId: string, rating: FeedbackRating) => void;
  onDialogSubmit: () => Promise<void>;
  onDialogSkip: () => Promise<void>;
  onDialogOpenChange: (open: boolean) => void;
}

export function useChatFeedback(): UseChatFeedbackResult {
  const chatStore = useChatStore();
  const [messageFeedback, setMessageFeedback] = useState<Record<string, FeedbackRating>>({});
  const [feedbackLoadingId, setFeedbackLoadingId] = useState<string | null>(null);
  const [feedbackDialog, setFeedbackDialog] = useState<{
    open: boolean;
    messageId: string | null;
  }>({ open: false, messageId: null });
  const [feedbackComment, setFeedbackComment] = useState("");

  const hydrate = useCallback((messages: Array<{ id: string; feedbackRating?: string | null }>) => {
    const feedback: Record<string, FeedbackRating> = {};
    for (const m of messages) {
      if (m.feedbackRating === "positive" || m.feedbackRating === "negative") {
        feedback[m.id] = m.feedbackRating;
      }
    }
    setMessageFeedback(feedback);
  }, []);

  const reset = useCallback(() => setMessageFeedback({}), []);

  const submitFeedback = useCallback(
    async (params: { messageId: string; rating: FeedbackRating; comment?: string }) => {
      setFeedbackLoadingId(params.messageId);
      try {
        await chatStore.submitFeedback(params);
        setMessageFeedback((prev) => ({ ...prev, [params.messageId]: params.rating }));
        toast.success("Thanks for your feedback!");
        return true;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : "Could not submit feedback";
        toast.error("Feedback failed", { description: errorMsg });
        return false;
      } finally {
        setFeedbackLoadingId(null);
      }
    },
    [chatStore]
  );

  const removeFeedback = useCallback(
    async (messageId: string) => {
      setFeedbackLoadingId(messageId);
      try {
        await chatStore.deleteFeedback(messageId);
        setMessageFeedback((prev) => {
          const next = { ...prev };
          delete next[messageId];
          return next;
        });
        toast.success("Feedback removed");
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : "Could not remove feedback";
        toast.error("Failed to remove feedback", { description: errorMsg });
      } finally {
        setFeedbackLoadingId(null);
      }
    },
    [chatStore]
  );

  const onMessageFeedback = useCallback(
    (messageId: string, rating: FeedbackRating) => {
      // Toggle off: clicking the same rating removes feedback
      if (messageFeedback[messageId] === rating) {
        removeFeedback(messageId);
        return;
      }
      if (rating === "positive") {
        submitFeedback({ messageId, rating });
        return;
      }
      setFeedbackDialog({ open: true, messageId });
      setFeedbackComment("");
    },
    [messageFeedback, submitFeedback, removeFeedback]
  );

  const closeDialog = useCallback(() => {
    setFeedbackDialog({ open: false, messageId: null });
    setFeedbackComment("");
  }, []);

  const onDialogSubmit = useCallback(async () => {
    if (!feedbackDialog.messageId) return;
    const comment = feedbackComment.trim();
    const success = await submitFeedback({
      messageId: feedbackDialog.messageId,
      rating: "negative",
      comment: comment.length ? comment : undefined,
    });
    if (success) closeDialog();
  }, [feedbackDialog.messageId, feedbackComment, submitFeedback, closeDialog]);

  const onDialogSkip = useCallback(async () => {
    if (!feedbackDialog.messageId) return;
    const success = await submitFeedback({
      messageId: feedbackDialog.messageId,
      rating: "negative",
    });
    if (success) closeDialog();
  }, [feedbackDialog.messageId, submitFeedback, closeDialog]);

  const onDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeDialog();
    },
    [closeDialog]
  );

  return {
    messageFeedback,
    feedbackLoadingId,
    feedbackDialog,
    feedbackComment,
    setFeedbackComment,
    hydrate,
    reset,
    onMessageFeedback,
    onDialogSubmit,
    onDialogSkip,
    onDialogOpenChange,
  };
}
